import type { Player } from '@/playback/api'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { documentEvents, windowEvents } from '@/util/interaction'
import { ctx } from '@/util/context'
import { getUserInfo } from '@/customer/service/userInfoService'

const { errorHandler } = ctx('inactivityMonitor')

const INACTIVITY_LIMIT_MS = 2 * 60 * 60 * 1000 // 2 hours

/**
 * Ghost-listener detection: tracks user interaction (and returning to a
 * backgrounded tab) and pauses playback at the first track transition after
 * 2 hours of inactivity. Shows the inactivity modal so the user can choose
 * to continue or stop; resume is user-initiated (we never auto-resume).
 *
 * Mirrors GH's GhostListenerStore + LocalPlaybackController inactivity
 * check, which pauses on the CurrentEntityChanged transition. One-shot
 * per app lifecycle (fires at most once). Unlimited customers are never
 * treated as inactive, matching the old webplayer.
 */
export function initializeInactivityMonitor(deps: {
    readonly playerPromise: Promise<Player>
}): Unsubscribe {
    let lastInteraction = Date.now()
    let fired = false
    const userInfo$ = getUserInfo()

    const resetTimer = () => {
        if (!fired) lastInteraction = Date.now()
    }

    const disposer = createDisposer()
    const { add, dispose } = disposer
    add(windowEvents.on('mousedown', resetTimer))
    add(windowEvents.on('keydown', resetTimer))
    add(windowEvents.on('click', resetTimer))
    // Returning to a backgrounded tab counts as the user coming back too —
    // mirrors playlistLiveUpdates' visibility seam.
    add(
        documentEvents.on('visibilitychange', () => {
            if (document.visibilityState === 'visible') resetTimer()
        })
    )

    deps.playerPromise
        .then((player) => {
            // A reload can dispose the monitor before the player resolves;
            // bail so we don't create (and leak) a subscription add() rejects.
            if (disposer.disposed) return

            // Gate on the false→true `playing$` edge (a new track actually
            // starting), NOT on `ended$`: the just-ended track isn't producing
            // audio, and `playerPlaylistBinding` asynchronously auto-advances to
            // the next track and plays it — a pause fired on `ended$` is clobbered
            // by that trailing play(). Pausing once the next track is playing is
            // the engine-agnostic analog of GH pausing at CurrentEntityChanged.
            let wasPlaying = player.playing$.value
            add(
                player.playing$.onData((playing) => {
                    const startedPlaying = playing && !wasPlaying
                    wasPlaying = playing
                    if (!startedPlaying || fired) return
                    if (Date.now() - lastInteraction < INACTIVITY_LIMIT_MS) return
                    if (userInfo$.value?.user?.tier === 'UNLIMITED') return

                    fired = true
                    player.pause()
                    // Lazy — the modal chrome only loads on the (rare) 2-hour
                    // inactivity trip, keeping it out of the app-shell chunk.
                    // Playback is already paused, so a slow load is harmless.
                    import('@/messaging/systemModals')
                        .then((m) =>
                            m.openInactivityModal({
                                onContinue: () => {
                                    player.play().catch(() => {})
                                },
                            })
                        )
                        .catch(() => {})
                })
            )
        })
        .catch(errorHandler)

    return dispose
}
